import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { APIRoutes } from '@/services/api.routes';
import { APIRequestError } from '@/services/api';
import { analytics, AnalyticsEvents } from '@/services/analytics';
import { handleAPIError, TOAST_MESSAGES } from '@/utils/toasts';
import type { ChatMessage, ChatMessageID, ChatSession } from '@/models';
import type { ListMessagesResponse } from '@/services/api.models';

// Polling-Intervalle (analog ChatWindow): schnell solange der LLM antwortet, sonst gemütlich
const POLL_INTERVAL_ACTIVE_MS = 1000;
const POLL_INTERVAL_IDLE_MS = 3000;

const SESSION_TITLE = 'Video-Finder';

export type VideoFinderStatus =
  | 'creating'    // Session wird gerade angelegt
  | 'ready'       // Session offen, Nachrichten können gesendet werden
  | 'closed'      // Session wurde (z.B. vom Backend-Job) geschlossen
  | 'expired'     // Session existiert nicht mehr (z.B. vom Delete-Empty-Chats-Job gelöscht)
  | 'createError'; // Session konnte nicht angelegt werden

export interface UseVideoFinderChatResult {
  session: ChatSession | null;
  messages: ChatMessage[];
  status: VideoFinderStatus;
  sending: boolean;
  backgroundError: string | null;
  clearBackgroundError: () => void;
  debugMode: boolean;
  setDebugMode: (value: boolean) => void;
  startNewSearch: () => Promise<void>;
  /** Liefert false, wenn das Senden fehlgeschlagen ist (damit die Eingabe wiederhergestellt werden kann) */
  sendMessage: (content: string) => Promise<boolean>;
}

function isEntityNotFoundError(err: unknown): boolean {
  return err instanceof APIRequestError && err.errorcode === 'ENTITY_NOT_FOUND';
}

function isChatClosedError(err: unknown): boolean {
  return err instanceof APIRequestError && err.errorcode === 'CHAT_CLOSED';
}

/**
 * Kapselt den kompletten Lebenszyklus einer Video-Finder-Suche:
 * Session anlegen, Nachrichten pollen, Nachrichten senden.
 *
 * Jede Suche ist eine frische VIDEO_SEARCH-Session - es gibt bewusst keine Möglichkeit,
 * eine alte Session wieder aufzunehmen.
 *
 * `active` bedeutet "der Video-Finder ist gerade sichtbar". Solange er zu ist, wird weder eine
 * Session angelegt noch gepollt - der State bleibt aber erhalten, weil der Hook im Provider lebt.
 */
export function useVideoFinderChat(active: boolean): UseVideoFinderChatResult {
  const [session, setSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Startwert 'creating': die Session wird erst beim ersten Öffnen angelegt, und genau dann ist
  // 'creating' auch korrekt (Spinner). Solange der Finder zu ist, liest den Status niemand.
  const [status, setStatus] = useState<VideoFinderStatus>('creating');
  const [sending, setSending] = useState(false);
  const [backgroundError, setBackgroundError] = useState<string | null>(null);
  const [debugMode, setDebugModeState] = useState(false);
  // Wird nach dem Senden erhöht, damit die Polling-Kette sofort neu startet statt auf den nächsten Idle-Tick zu warten
  const [pollGeneration, setPollGeneration] = useState(0);

  // Refs statt State, damit der Polling-Effekt nicht bei jeder Nachricht neu aufgesetzt wird
  const lastMessageIdRef = useRef<ChatMessageID | null>(null);
  const llmActiveRef = useRef(false);
  // Generationszähler: verwirft veraltete Ergebnisse von createChatSession (StrictMode-Doppelmount, Doppelklick)
  const createGenerationRef = useRef(0);
  // Merkt sich, ob für diese Provider-Instanz schon einmal eine Session gestartet wurde.
  // Wird beim Schließen bewusst NICHT zurückgesetzt - erneutes Öffnen setzt die Suche fort.
  const startedRef = useRef(false);

  useEffect(() => {
    lastMessageIdRef.current = messages.length > 0 ? messages[messages.length - 1].id : null;
  }, [messages]);

  const startNewSearch = useCallback(async () => {
    createGenerationRef.current += 1;
    const generation = createGenerationRef.current;

    setStatus('creating');
    setSession(null);
    setMessages([]);
    setBackgroundError(null);
    lastMessageIdRef.current = null;
    llmActiveRef.current = false;

    try {
      const created = await APIRoutes.createChatSession({ title: SESSION_TITLE, type: 'VIDEO_SEARCH' });
      if (generation !== createGenerationRef.current) {
        return; // inzwischen wurde eine neuere Suche gestartet
      }

      setSession(created);
      setStatus('ready');
      analytics.track(AnalyticsEvents.CHAT_STARTED, {
        sessionId: created.id,
        type: 'VIDEO_SEARCH',
      });
    } catch (err) {
      if (generation !== createGenerationRef.current) {
        return;
      }
      setStatus('createError');
      handleAPIError(err, TOAST_MESSAGES.CHAT_CREATE_ERROR);
    }
  }, []);

  // Session erst beim ersten Öffnen anlegen, nicht schon beim Mount des Providers - sonst würde
  // jeder Seitenaufruf eine leere VIDEO_SEARCH-Session erzeugen. Der Ref-Guard deckt zusätzlich
  // den StrictMode-Doppelmount ab.
  useEffect(() => {
    if (!active || startedRef.current) {
      return;
    }
    startedRef.current = true;
    void startNewSearch();
  }, [active, startNewSearch]);

  const applyMessagesResponse = useCallback((resp: ListMessagesResponse) => {
    setMessages(prev => {
      const knownIds = new Set(prev.map(m => m.id));
      const fresh = resp.messages.filter(m => !knownIds.has(m.id));
      return fresh.length === 0 ? prev : [...prev, ...fresh];
    });

    llmActiveRef.current = resp.chatSession.llmActive;

    // Session nur ersetzen, wenn sich etwas Relevantes geändert hat (verhindert unnötige Re-Renders)
    setSession(prev => {
      if (prev !== null && prev.llmActive === resp.chatSession.llmActive && prev.closed === resp.chatSession.closed) {
        return prev;
      }
      return resp.chatSession;
    });
  }, []);

  // Polling als setTimeout-Kette: der nächste Tick wird erst nach Abschluss des Requests geplant,
  // dadurch gibt es keine überlappenden Requests. Hängt bewusst nur an der Session-ID, nicht am
  // Session-Objekt - sonst würde jede llmActive-Änderung die Kette neu starten.
  const sessionId = session?.id ?? null;
  useEffect(() => {
    // Solange der Finder zu ist, wird nicht gepollt. Beim Öffnen startet die Kette sofort wieder
    // (void tick() unten) und holt über lastMessageIdRef alles nach, was zwischenzeitlich entstand.
    if (!active || sessionId === null || status !== 'ready') {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let wasActive = llmActiveRef.current;

    const tick = async () => {
      if (cancelled) {
        return;
      }

      try {
        const resp = await APIRoutes.listMessages(sessionId, lastMessageIdRef.current ?? undefined, debugMode);
        if (cancelled) {
          return;
        }

        applyMessagesResponse(resp);
        setBackgroundError(null);

        if (resp.chatSession.closed) {
          setStatus('closed');
          return;
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        if (isEntityNotFoundError(err)) {
          setStatus('expired');
          return;
        }
        console.error('Error fetching video finder messages:', err);
        setBackgroundError('Fehler beim Abrufen neuer Nachrichten');
      }

      // Nach dem Wechsel aktiv -> inaktiv noch einen schnellen Tick, falls die letzte Nachricht
      // erst kurz nach dem Zurücksetzen von llmActive sichtbar wird.
      const active = llmActiveRef.current;
      const delay = (active || wasActive) ? POLL_INTERVAL_ACTIVE_MS : POLL_INTERVAL_IDLE_MS;
      wasActive = active;
      timer = setTimeout(() => { void tick(); }, delay);
    };

    void tick();

    return () => {
      cancelled = true;
      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, [active, sessionId, status, debugMode, pollGeneration, applyMessagesResponse]);

  const setDebugMode = useCallback((value: boolean) => {
    // Komplett neu laden, weil im Debug-Modus zusätzliche Nachrichtentypen (Tool-Calls etc.) kommen
    setMessages([]);
    lastMessageIdRef.current = null;
    setDebugModeState(value);
  }, []);

  const sendMessage = useCallback(async (content: string): Promise<boolean> => {
    if (session === null || status !== 'ready' || sending || llmActiveRef.current) {
      return false;
    }

    setSending(true);
    try {
      const resp = await APIRoutes.sendMessage(session.id, {
        content,
        llmLevel: 'FULL',
        allowTools: true,
      });

      setMessages(prev => prev.some(m => m.id === resp.userMessage.id) ? prev : [...prev, resp.userMessage]);
      llmActiveRef.current = resp.chatSession.llmActive;
      setSession(resp.chatSession);
      setPollGeneration(g => g + 1);

      analytics.track(AnalyticsEvents.CHAT_MESSAGE_SENT, {
        sessionId: session.id,
        sessionType: 'VIDEO_SEARCH',
        hasImages: false,
        imageCount: 0,
        llmLevel: 'FULL',
        allowTools: true,
      });

      return true;
    } catch (err) {
      if (isChatClosedError(err)) {
        setStatus('closed');
      } else if (isEntityNotFoundError(err)) {
        setStatus('expired');
      }
      handleAPIError(err, TOAST_MESSAGES.MESSAGE_SEND_ERROR);
      return false;
    } finally {
      setSending(false);
    }
  }, [session, status, sending]);

  const clearBackgroundError = useCallback(() => setBackgroundError(null), []);

  // Memoisiert, weil das Ergebnis direkt in den Context-Wert des VideoFinderProviders fliesst -
  // ein frisches Objektliteral pro Render würde dort alle Consumer neu rendern lassen.
  return useMemo(() => ({
    session,
    messages,
    status,
    sending,
    backgroundError,
    clearBackgroundError,
    debugMode,
    setDebugMode,
    startNewSearch,
    sendMessage,
  }), [
    session,
    messages,
    status,
    sending,
    backgroundError,
    clearBackgroundError,
    debugMode,
    setDebugMode,
    startNewSearch,
    sendMessage,
  ]);
}
